import { ColumnDef } from '@tanstack/react-table'
import { Check, Loader2, RotateCw, Search } from 'lucide-react'
import { useState } from 'react'

import { Table } from '@/components/common/data-display/Table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { useAuthStore } from '@/features/sessao/store/useAuthStore'
import { useUsuarios } from '@/features/usuarios/hooks/use-usuarios'
import { useDebounce_new } from '@/hooks/useDebounce'
import { User, user_role } from '@/services/backend/config/entities'

type SafeUser = Omit<User, 'password'>

type Props = {
  role?: user_role
  onSelect: (data: SafeUser) => void
}

function Form({ role, onSelect }: Props) {
  const { user } = useAuthStore()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce_new(search)

  const {
    query: { data, isFetching, refetch },
  } = useUsuarios(debouncedSearch, role)

  const columns: ColumnDef<SafeUser>[] = [
    {
      header: 'Nome',
      accessorKey: 'name',
      sortingFn: (a, b) =>
        a.original.displayName > b.original.displayName ? 1 : -1,
      cell: ({ row }) => {
        const isMe = row.original.id === user?.id

        return (
          <div className="flex items-center gap-2">
            <span className={isMe ? 'font-bold' : 'font-normal'}>
              {row.original.displayName}
            </span>
            {isMe && <Badge>Eu</Badge>}
          </div>
        )
      },
    },
    {
      header: '',
      accessorKey: 'id',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button
            size="icon"
            aria-label="Selecionar"
            onClick={() => onSelect(row.original)}
          >
            <Check className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <>
      <div className="relative mb-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

        <Input
          className="px-9"
          placeholder="Pesquisar"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="absolute right-1 top-1/2 -translate-y-1/2">
          {isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <Button
              aria-label="Pesquisar"
              variant="ghost"
              size="icon"
              onClick={() => refetch()}
            >
              <RotateCw className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <Table
        data={data ?? []}
        columns={columns}
        initialSorting={[{ id: 'name', desc: false }]}
      />
    </>
  )
}

export default function Component({ role, onSelect }: Props) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button aria-label="Pesquisar" size="icon">
          <Search className="h-4 w-4" />
        </Button>
      </SheetTrigger>

      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Usuários</SheetTitle>
        </SheetHeader>

        <div className="mt-4">
          <Form
            role={role}
            onSelect={(res) => {
              onSelect(res)
              setIsOpen(false)
            }}
          />
        </div>
      </SheetContent>
    </Sheet>
  )
}
